/*
 * מנוע קופסאות — נפח בלי לצייר 48 ספרייטים ביד.
 *
 * **הרעיון:** יחידה אינה ציור אלא **תיאור**. טנק הוא חמש קופסאות — שתי שרשראות,
 * גוף, צריח וקנה — עם מידות ומיקום. המנוע מסובב את הקודקודים, מטיל אותם למסך,
 * ומצייר את הפאות מהרחוקה לקרובה עם הצללה לפי כיוון האור.
 *
 * זה מה שמשנה את סדר הגודל: **16 הזוויות מגיעות בחינם ועקביות**, כי הן חישוב
 * ולא ציור. הניסיון לצייר אותן ביד נכשל בדיוק בזה — צללית של טנק מלפנים-שמאל
 * היא צורה אחרת לגמרי מצללית מהצד, ואי אפשר פשוט לסובב ציור אחד.
 *
 * **מערכת הצירים:** x קדימה (כיוון הפנייה), y ימינה, z למעלה. z=0 הוא הקרקע.
 * ההיטל הוא מבט-על עם גובה: הגובה דוחף את הפיקסל **למעלה במסך**, וכך נראות
 * הפאות הצדדיות ומתקבל נפח בלי לשנות את ההיטל של המשחק.
 *
 * הכול כאן ציור בלבד. הסימולציה אינה יודעת שקיימות קופסאות, בדיוק כפי שאינה
 * יודעת שקיים מסך.
 */

/**
 * כמה פיקסלים "עולה" יחידת גובה אחת על המסך.
 *
 * נמוך מדי והיחידה שטוחה כמו קודם; גבוה מדי והיא נראית כמו מגדל שנפל. 0.62 הוא
 * הערך שבו טנק נקרא כטנק — רואים את דופן הגוף ואת דופן הצריח, והצללית עדיין
 * מתיישבת עם הרדיוס שבתוכן.
 */
const HEIGHT_SCALE = 0.62;

/**
 * כיוון האור בעולם, מנורמל. מצפון-מערב ומלמעלה — כמו ברוב משחקי המבט-העל.
 *
 * **הוא נשאר קבוע בעולם ולא ביחידה.** אילו האור היה מחושב בקואורדינטות מקומיות,
 * הוא היה מסתובב יחד עם כל יחידה, וכל השדה היה נראה כאילו יש בו עשרים שמשות.
 */
const LIGHT: readonly [number, number, number] = [-0.5, -0.55, 0.67];

/**
 * גוף בודד במודל. המידות ביחידות התא של ה-atlas.
 *
 * למרות השם, זו אינה בהכרח קופסה: `round` הופך את הבסיס למעוגל ו-`taper` מצר
 * את הפאה העליונה. **בלי שני אלה כל דבר נראה כמו רובוט** — וזה בדיוק מה שקרה
 * לחי"ר בגרסה הראשונה. רכבים נשארים חדים; אנשים לא.
 */
export interface Box {
    /** מרכז הבסיס. `z` הוא הגובה שבו הגוף מתחיל. */
    readonly x: number;
    readonly y: number;
    readonly z: number;
    /** אורך (קדימה), רוחב (לצדדים), גובה. */
    readonly length: number;
    readonly width: number;
    readonly height: number;
    /** 0 = פינות חדות (קופסה), 1 = בסיס אליפטי (גליל). */
    readonly round?: number;
    /** קנה מידה של הפאה העליונה ביחס לתחתונה. 1 = ישר, קטן מ-1 = משופע כלפי מעלה. */
    readonly taper?: number;
    /** סיבוב מקומי סביב z, ברדיאנים — לזרוע מנוף או לפרט אלכסוני. */
    readonly yaw?: number;
    /**
     * הבהרה או הכהיה של צבע השחקן עבור הגוף הזה.
     * ‏`fixed` דורס לגמרי — לשרשראות ולקנה, שאינם צבועים בצבע הקבוצה.
     */
    readonly shade?: number;
    readonly fixed?: string;
}

/** כמה קודקודים לבסיס מעוגל. 16 מספיקים כדי שגליל ייראה עגול בגודל של יחידה. */
const ROUND_STEPS = 16;

/**
 * הבסיס במישור המקומי.
 *
 * ‏`round=0` מחזיר ארבע פינות — קופסה מדויקת, בלי עלות. מעליו זו על-אליפסה:
 * מעבר רציף בין מלבן לאליפסה, שמאפשר לתאר גם קסדה וגם מכל דלק באותו מנגנון.
 */
function footprint(box: Box): readonly (readonly [number, number])[] {
    const halfLength = box.length / 2;
    const halfWidth = box.width / 2;
    const round = box.round ?? 0;

    if (round <= 0) {
        return [
            [-halfLength, -halfWidth],
            [halfLength, -halfWidth],
            [halfLength, halfWidth],
            [-halfLength, halfWidth],
        ];
    }

    // n=2 הוא אליפסה; n גדול מתקרב למלבן.
    const n = 2 + (1 - round) * 18;
    const points: [number, number][] = [];
    for (let i = 0; i < ROUND_STEPS; i++) {
        const t = (i / ROUND_STEPS) * Math.PI * 2;
        const c = Math.cos(t);
        const s = Math.sin(t);
        points.push([
            Math.sign(c) * Math.pow(Math.abs(c), 2 / n) * halfLength,
            Math.sign(s) * Math.pow(Math.abs(s), 2 / n) * halfWidth,
        ]);
    }
    return points;
}

export type BoxModel = readonly Box[];

type Point = { readonly x: number; readonly y: number };

/** פאה מוכנה לציור: מצולע במסך, עוצמת תאורה, ועומק למיון. */
interface Face {
    readonly points: readonly Point[];
    readonly light: number;
    readonly depth: number;
}

/**
 * מצייר מודל קופסאות סביב הראשית, מסובב בזווית נתונה.
 *
 * הקורא אחראי ל-`translate` ול-`scale`; כאן הכול בקואורדינטות המודל.
 */
export function drawBoxModel(
    ctx: CanvasRenderingContext2D,
    model: BoxModel,
    angle: number,
    color: string,
): void {
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);

    /*
     * הקופסאות ממוינות מהנמוכה לגבוהה ואז מהרחוקה לקרובה.
     *
     * הגובה קודם, כי צריח יושב **על** הגוף ותמיד מצויר אחריו. בין קופסאות באותו
     * גובה מכריע העומק. זה אלגוריתם הצייר, והוא נכון כאן משום שהמודלים קמורים
     * ואינם משתלבים זה בזה.
     */
    const ordered = [...model]
        .map((box) => ({
            box,
            depth: box.x * sin + box.y * cos - box.z * HEIGHT_SCALE,
        }))
        .sort((a, b) => a.box.z - b.box.z || a.depth - b.depth);

    for (const { box } of ordered) drawBox(ctx, box, cos, sin, color);
}

function drawBox(
    ctx: CanvasRenderingContext2D,
    box: Box,
    cos: number,
    sin: number,
    color: string,
): void {
    const base = footprint(box);
    const taper = box.taper ?? 1;

    // סיבוב מקומי (yaw) מורכב על סיבוב היחידה, כדי שפרט אלכסוני יסתובב איתה.
    const yaw = box.yaw ?? 0;
    const yc = Math.cos(yaw);
    const ys = Math.sin(yaw);

    const project = (lx: number, ly: number, lz: number): Point => {
        const rx = lx * yc - ly * ys;
        const ry = lx * ys + ly * yc;
        const wx = (box.x + rx) * cos - (box.y + ry) * sin;
        const wy = (box.x + rx) * sin + (box.y + ry) * cos;
        // הגובה דוחף למעלה במסך — זה כל ההיטל.
        return { x: wx, y: wy - (box.z + lz) * HEIGHT_SCALE };
    };

    const bottom = base.map(([lx, ly]) => project(lx, ly, 0));
    const top = base.map(([lx, ly]) => project(lx * taper, ly * taper, box.height));

    const faces: Face[] = [];

    // ---- פאות צדדיות: אחת לכל צלע בבסיס
    for (let i = 0; i < base.length; i++) {
        const a = base[i];
        const b = base[(i + 1) % base.length];
        const bottomA = bottom[i];
        const bottomB = bottom[(i + 1) % base.length];
        const topA = top[i];
        const topB = top[(i + 1) % base.length];
        if (!a || !b || !bottomA || !bottomB || !topA || !topB) continue;

        /*
         * הנורמל של הצלע במישור, מסובב לעולם **לפני** חישוב התאורה. פאה שפונה
         * צפונה מוארת בין אם היחידה פונה צפונה ובין אם מזרחה — וזו בדיוק
         * המשמעות של "אור שקבוע בעולם".
         */
        const edgeX = b[0] - a[0];
        const edgeY = b[1] - a[1];
        const localNx = edgeY;
        const localNy = -edgeX;
        const rotNx = localNx * yc - localNy * ys;
        const rotNy = localNx * ys + localNy * yc;
        const nx = rotNx * cos - rotNy * sin;
        const ny = rotNx * sin + rotNy * cos;
        const length = Math.hypot(nx, ny) || 1;

        // פאה שפונה מהצופה אינה נראית ואינה מצוירת.
        if (ny <= 0) continue;

        const lambert = Math.max(0, (nx / length) * LIGHT[0] + (ny / length) * LIGHT[1]);
        const points = [bottomA, bottomB, topB, topA];
        const depth = points.reduce((sum, point) => sum + point.y, 0) / points.length;
        faces.push({ points, light: lambert, depth });
    }

    faces.sort((a, b) => a.depth - b.depth);
    /*
     * הגג תמיד אחרון: הוא לעולם אינו מוסתר על ידי הפאות של אותו גוף.
     *
     * הוא מקבל תאורה מלאה ולא את רכיב ה-z של האור בלבד. **הניגודיות בין הגג
     * לקירות היא כל הנפח:** כשהם קרובים בבהירות המבנה נראה שוב כמו מלבן צבוע.
     */
    faces.push({ points: top, light: 1, depth: Infinity });

    const paint = box.fixed ?? color;
    for (const face of faces) {
        /*
         * טווח ההצללה: 0.42 מתחת לצבע הבסיס ועד 0.3 מעליו. פחות מזה והנפח נעלם;
         * יותר והפאה הכהה נראית כמו חור.
         */
        const amount = -0.5 + face.light * 0.9 + (box.shade ?? 0);
        ctx.fillStyle = tint(paint, Math.max(-0.68, Math.min(0.5, amount)));
        ctx.beginPath();
        face.points.forEach((point, index) => {
            if (index === 0) ctx.moveTo(point.x, point.y);
            else ctx.lineTo(point.x, point.y);
        });
        ctx.closePath();
        ctx.fill();

        // מתאר דק לכל פאה — קווי לוחות, וגם מה שמונע מהיחידה להיבלע ברקע.
        ctx.strokeStyle = tint(paint, -0.72);
        ctx.lineWidth = 1.6;
        ctx.stroke();
    }
}

/** מבהיר או מכהה צבע. amount חיובי מבהיר, שלילי מכהה. */
function tint(hex: string, amount: number): string {
    if (!hex.startsWith('#')) return hex;
    const value = parseInt(hex.slice(1), 16);
    const channel = (shift: number) => {
        const base = (value >> shift) & 0xff;
        const target = amount >= 0 ? 255 : 0;
        return Math.round(base + (target - base) * Math.abs(amount));
    };
    return `rgb(${channel(16)},${channel(8)},${channel(0)})`;
}
